"use client";

import Link from "next/link";
import { MobileNav } from "./mobile-nav";
import { AvatarDropdown } from "./avatar-dropdown";
import { SavedDrawerPeek } from "./saved-drawer-peek";
import { LocationPill } from "./location-search";
import { primaryNavLinks, isNavLinkActive } from "@/lib/nav/primary-nav";
import {
  accountRoleForChrome,
  accountTierForChrome,
  locationSlugFromPath,
  type ChromeState,
} from "@/lib/nav/chrome-state";

/**
 * Global top bar — discovery + info nav (#1011).
 *
 *   WILDROOST logo · 📍 Location · Find Camps · For Providers · About · Avatar ▾
 *
 * Saved / Planner / Kids / Reviews live inside the tier-aware account dropdown
 * (the "My Family" hub). Anonymous users see Sign in / Create account.
 *
 * 64px height, full-bleed, backdrop-blur(20px).
 */
export function TopBar({
  state,
  pathname,
}: {
  state: ChromeState;
  pathname: string;
}) {
  const locationSlug = locationSlugFromPath(pathname) ?? state.locationSlug;
  const locationName = state.locationName;
  const initial = (state.displayName ?? state.email ?? "U").charAt(0).toUpperCase();
  const tier = accountTierForChrome(state);
  const role = accountRoleForChrome(state);

  /** Active-state pill class for nav links */
  function navLinkClass(active: boolean): string {
    return active
      ? "rounded-lg bg-wildroost-teal/10 px-3 py-2 text-sm font-semibold text-wildroost-teal md:text-base"
      : "rounded-lg px-3 py-2 text-sm font-semibold text-wildroost-ink/80 hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal md:text-base";
  }

  return (
    <>
    <header className={`fixed ${state.isAdmin ? "top-7" : "top-0"} z-[200] w-full border-b border-wildroost-ink/10 bg-white/[.98] backdrop-blur-[20px]`}>
      <nav
        aria-label="Primary"
        className="mx-auto flex w-full items-center justify-between gap-4 px-6 md:px-10"
        style={{ height: 64 }}
      >
        {/* Logo + Location pill — left-aligned together */}
        <div className="flex shrink-0 items-center gap-2">
          <Link
            href="/"
            className="flex shrink-0 items-center"
            aria-label="Wildroost home"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- Public SVG logo should render directly without Next image proxying. */}
            <img
              src="/brand/primary-hero-no-tagline-white-v3.svg"
              alt="Wildroost"
              width={152}
              height={32}
            />
          </Link>
          <LocationPill slug={locationSlug} name={locationName} />
        </div>

        {/* Mobile hamburger menu — visible below md (#1011) */}
        <MobileNav
          tier={tier}
          role={role}
          displayName={state.displayName}
          initial={initial}
          isProvider={state.isProvider}
          savedCount={state.savedCampCount}
          pathname={pathname}
          locationSlug={locationSlug}
          locationName={locationName}
        />

        {/* Desktop nav — hidden below md. Discovery + info links, then the
            tier-aware account avatar (Saved / Planner / etc. live inside it). */}
        <div className="hidden items-center gap-1 md:flex md:gap-2">
          {primaryNavLinks().map((link) => {
            const active = isNavLinkActive(link.href, pathname);
            return (
              <Link
                key={link.href}
                href={link.href}
                aria-current={active ? "page" : undefined}
                className={navLinkClass(active)}
              >
                {link.label}
              </Link>
            );
          })}

          <AvatarDropdown
            tier={tier}
            role={role}
            displayName={state.displayName}
            initial={initial}
            isProvider={state.isProvider}
            savedCount={state.savedCampCount}
            pathname={pathname}
          />
        </div>
      </nav>
    </header>
    {state.isSignedIn && <SavedDrawerPeek />}
    </>
  );
}
